import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { fetchVacancySpend } from '../services/vacancySpendService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatDate = (value) => {
  if (!value) return 'Open';
  return value;
};

const parseISODate = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, d.getDate());

const monthsBetweenInclusive = (from, to) => {
  const out = [];
  let cur = startOfMonth(from);
  const last = startOfMonth(to);
  while (cur <= last) {
    out.push(endOfMonth(cur));
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return out;
};

/** Average + count for closed vacancies of `kind` with close date in (asOf - months, asOf]. */
const trailingStats = (closedRows, kind, asOf, months) => {
  const windowStart = addMonths(asOf, -months);
  const subset = closedRows.filter((row) => {
    if (row.kind !== kind) return false;
    const close = parseISODate(row.vacancy_end);
    if (!close) return false;
    return close > windowStart && close <= asOf;
  });
  if (!subset.length) return { avg: null, n: 0 };
  const sum = subset.reduce((acc, row) => acc + (Number(row.total) || 0), 0);
  return { avg: sum / subset.length, n: subset.length };
};

const KindBadge = ({ kind }) => {
  const isReno = kind === 'renovation';
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
        isReno
          ? 'bg-amber-100 text-amber-900'
          : 'bg-slate-100 text-slate-800'
      }`}
    >
      {isReno ? 'Renovation' : 'Turnover'}
    </span>
  );
};

const SORTABLE_COLUMNS = [
  { key: 'property_address', label: 'Property', align: 'left' },
  { key: 'owner', label: 'Owner', align: 'left' },
  { key: 'kind', label: 'Type', align: 'left' },
  { key: 'vacancy_start', label: 'Vacancy Start', align: 'left' },
  { key: 'vacancy_end', label: 'Vacancy End', align: 'left' },
  { key: 'days_vacant', label: 'Days', align: 'right' },
  { key: 'labor', label: 'Labor', align: 'right' },
  { key: 'materials', label: 'Materials', align: 'right' },
  { key: 'materials_last_7_days', label: 'Materials (7d)', align: 'right' },
  { key: 'total', label: 'Total', align: 'right' },
];

const commonChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 },
        boxWidth: 10,
      },
    },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed.y;
          const n = ctx.dataset.counts?.[ctx.dataIndex] ?? 0;
          const noun = n === 1 ? 'property' : 'properties';
          if (v == null) return `${ctx.dataset.label}: — (0 properties)`;
          return `${ctx.dataset.label}: ${formatCurrency(v)} (${n} ${noun})`;
        },
      },
    },
  },
  layout: {
    padding: { left: 10, right: 20, top: 0, bottom: 10 },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value) => `$${Number(value).toLocaleString()}`,
        font: { size: 12 },
        maxTicksLimit: 8,
      },
      grid: { color: 'rgba(0, 0, 0, 0.05)' },
    },
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        font: { size: 12 },
        maxTicksLimit: 12,
      },
      grid: { display: false },
    },
  },
};

const VacancySpend = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [sortKey, setSortKey] = useState('total');
  const [sortDesc, setSortDesc] = useState(true);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchVacancySpend({ signal });
      if (signal?.aborted) return;
      const withSpend = (Array.isArray(data) ? data : []).filter(
        (row) => Number(row.total) > 0
      );
      setRows(withSpend);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err.message || 'Failed to load vacancy spend');
      setRows([]);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDesc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const closedRows = useMemo(
    () => rows.filter((row) => row.vacancy_end && Number(row.total) > 0),
    [rows]
  );

  const trailingSummary = useMemo(() => {
    const asOf = new Date();
    asOf.setHours(23, 59, 59, 999);
    return {
      turnover3: trailingStats(closedRows, 'turnover', asOf, 3),
      turnover12: trailingStats(closedRows, 'turnover', asOf, 12),
      renovation3: trailingStats(closedRows, 'renovation', asOf, 3),
      renovation12: trailingStats(closedRows, 'renovation', asOf, 12),
    };
  }, [closedRows]);

  const trailingSeries = useMemo(() => {
    if (!closedRows.length) return [];
    const closes = closedRows
      .map((r) => parseISODate(r.vacancy_end))
      .filter(Boolean)
      .sort((a, b) => a - b);
    const first = closes[0];
    const last = endOfMonth(new Date());
    return monthsBetweenInclusive(first, last).map((monthEndDate) => {
      const label = monthEndDate.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
      return {
        label,
        turnover3: trailingStats(closedRows, 'turnover', monthEndDate, 3),
        turnover12: trailingStats(closedRows, 'turnover', monthEndDate, 12),
        renovation3: trailingStats(closedRows, 'renovation', monthEndDate, 3),
        renovation12: trailingStats(closedRows, 'renovation', monthEndDate, 12),
      };
    });
  }, [closedRows]);

  const turnoverChartData = useMemo(
    () => ({
      labels: trailingSeries.map((p) => p.label),
      datasets: [
        {
          label: 'Trailing 3-mo avg',
          data: trailingSeries.map((p) => p.turnover3.avg),
          counts: trailingSeries.map((p) => p.turnover3.n),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          fill: false,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
        },
        {
          label: 'Trailing 12-mo avg',
          data: trailingSeries.map((p) => p.turnover12.avg),
          counts: trailingSeries.map((p) => p.turnover12.n),
          borderColor: 'rgb(153, 102, 255)',
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
          fill: false,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
        },
      ],
    }),
    [trailingSeries]
  );

  const renovationChartData = useMemo(
    () => ({
      labels: trailingSeries.map((p) => p.label),
      datasets: [
        {
          label: 'Trailing 3-mo avg',
          data: trailingSeries.map((p) => p.renovation3.avg),
          counts: trailingSeries.map((p) => p.renovation3.n),
          borderColor: 'rgb(255, 159, 64)',
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          fill: false,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
        },
        {
          label: 'Trailing 12-mo avg',
          data: trailingSeries.map((p) => p.renovation12.avg),
          counts: trailingSeries.map((p) => p.renovation12.n),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          fill: false,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
        },
      ],
    }),
    [trailingSeries]
  );

  const turnoverChartOptions = useMemo(
    () => ({
      ...commonChartOptions,
      plugins: {
        ...commonChartOptions.plugins,
        title: {
          display: true,
          text: 'Avg Cost per Turnover (by close date)',
          font: { size: 16, weight: 'bold' },
          padding: { top: 10, bottom: 20 },
        },
      },
    }),
    []
  );

  const renovationChartOptions = useMemo(
    () => ({
      ...commonChartOptions,
      plugins: {
        ...commonChartOptions.plugins,
        title: {
          display: true,
          text: 'Avg Cost per Renovation (by close date)',
          font: { size: 16, weight: 'bold' },
          padding: { top: 10, bottom: 20 },
        },
      },
    }),
    []
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows.filter((row) => {
      if (Number(row.total) <= 0) return false;
      if (kindFilter !== 'all' && row.kind !== kindFilter) return false;
      if (!q) return true;
      return (
        (row.property_address || '').toLowerCase().includes(q) ||
        (row.owner || '').toLowerCase().includes(q)
      );
    });

    const dir = sortDesc ? -1 : 1;
    list = [...list].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === 'vacancy_end') {
        av = av || '9999-12-31';
        bv = bv || '9999-12-31';
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
    return list;
  }, [rows, kindFilter, search, sortKey, sortDesc]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, row) => {
        acc.labor += Number(row.labor) || 0;
        acc.materials += Number(row.materials) || 0;
        acc.total += Number(row.total) || 0;
        return acc;
      },
      { labor: 0, materials: 0, total: 0 }
    );
  }, [filtered]);

  const toggleExpand = (row) => {
    setExpandedId((prev) => (prev === row.id ? null : row.id));
  };

  const SortHeader = ({ col }) => {
    const active = sortKey === col.key;
    return (
      <th
        className={`px-3 py-2 font-medium cursor-pointer select-none hover:text-gray-900 ${
          col.align === 'right' ? 'text-right' : 'text-left'
        }`}
        onClick={() => handleSort(col.key)}
      >
        <span className="inline-flex items-center gap-1">
          {col.label}
          {active ? (
            sortDesc ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronUp className="w-3.5 h-3.5" />
            )
          ) : null}
        </span>
      </th>
    );
  };

  const MetricCard = ({ label, value, count, hint }) => (
    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 min-w-[160px]">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-1">
        {formatCurrency(value)}
      </div>
      <div className="text-xs text-gray-400 mt-1">
        {count != null
          ? `${count} ${count === 1 ? 'property' : 'properties'}`
          : null}
        {hint ? (count != null ? ` · ${hint}` : hint) : null}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Vacancy Spend</h2>
          <p className="text-sm text-gray-600 mt-1">
            Labor and materials from the general ledger during vacancy windows
            (property-level). Expand a row for vendor drill-down.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {!loading && !error && (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <MetricCard
              label="Turnover · 3 mo"
              value={trailingSummary.turnover3.avg}
              count={trailingSummary.turnover3.n}
              hint="by close date"
            />
            <MetricCard
              label="Turnover · 12 mo"
              value={trailingSummary.turnover12.avg}
              count={trailingSummary.turnover12.n}
              hint="by close date"
            />
            <MetricCard
              label="Renovation · 3 mo"
              value={trailingSummary.renovation3.avg}
              count={trailingSummary.renovation3.n}
              hint="by close date"
            />
            <MetricCard
              label="Renovation · 12 mo"
              value={trailingSummary.renovation12.avg}
              count={trailingSummary.renovation12.n}
              hint="by close date"
            />
          </div>

          {trailingSeries.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-4 h-80">
                <Line data={turnoverChartData} options={turnoverChartOptions} />
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 h-80">
                <Line data={renovationChartData} options={renovationChartOptions} />
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          placeholder="Search address or owner…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[220px]"
        />
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 text-sm"
        >
          <option value="all">All types</option>
          <option value="turnover">Turnover</option>
          <option value="renovation">Renovation</option>
        </select>
      </div>

      {!loading && !error && (
        <div className="flex flex-wrap gap-6 text-sm text-gray-700 mb-4">
          <div>
            <span className="text-gray-500">Rows:</span> {filtered.length}
          </div>
          <div>
            <span className="text-gray-500">Labor:</span> {formatCurrency(totals.labor)}
          </div>
          <div>
            <span className="text-gray-500">Materials:</span>{' '}
            {formatCurrency(totals.materials)}
          </div>
          <div>
            <span className="text-gray-500">Total:</span> {formatCurrency(totals.total)}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-gray-500">
          Loading vacancy spend from AppFolio GL… this can take a minute.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium w-8" />
                {SORTABLE_COLUMNS.map((col) => (
                  <SortHeader key={col.key} col={col} />
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                    No vacancy spend windows found.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => {
                  const open = expandedId === row.id;
                  const vendors = row.vendors || [];
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${
                          open ? 'bg-blue-50/40' : ''
                        }`}
                        onClick={() => toggleExpand(row)}
                      >
                        <td className="px-3 py-2 text-gray-400">
                          {open ? '▾' : '▸'}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {row.property_address}
                          {row.is_open && (
                            <span className="ml-2 text-xs text-green-700">Open</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{row.owner}</td>
                        <td className="px-3 py-2">
                          <KindBadge kind={row.kind} />
                        </td>
                        <td className="px-3 py-2">{formatDate(row.vacancy_start)}</td>
                        <td className="px-3 py-2">{formatDate(row.vacancy_end)}</td>
                        <td className="px-3 py-2 text-right">{row.days_vacant}</td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(row.labor)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(row.materials)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right ${
                            Number(row.materials_last_7_days) > 0
                              ? 'font-semibold text-amber-800 bg-amber-50'
                              : ''
                          }`}
                        >
                          {formatCurrency(row.materials_last_7_days)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatCurrency(row.total)}
                        </td>
                      </tr>
                      {open && (
                        <tr className="border-t border-gray-100 bg-slate-50">
                          <td colSpan={11} className="px-6 py-3">
                            <div className="text-xs text-gray-500 mb-2">
                              Spend window {row.spend_start} → {row.spend_end}
                            </div>
                            {vendors.length === 0 ? (
                              <div className="text-sm text-gray-500">
                                No classified vendor spend in this window.
                              </div>
                            ) : (
                              <table className="min-w-[480px] text-sm">
                                <thead className="text-gray-500 text-left">
                                  <tr>
                                    <th className="pr-4 py-1 font-medium">Vendor</th>
                                    <th className="pr-4 py-1 font-medium text-right">
                                      Labor
                                    </th>
                                    <th className="pr-4 py-1 font-medium text-right">
                                      Materials
                                    </th>
                                    <th className="py-1 font-medium text-right">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {vendors.map((v) => (
                                    <tr key={v.vendor} className="border-t border-gray-200">
                                      <td className="pr-4 py-1">{v.vendor}</td>
                                      <td className="pr-4 py-1 text-right">
                                        {formatCurrency(v.labor)}
                                      </td>
                                      <td className="pr-4 py-1 text-right">
                                        {formatCurrency(v.materials)}
                                      </td>
                                      <td className="py-1 text-right font-medium">
                                        {formatCurrency(v.total)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VacancySpend;
